import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SidebarLayout from "../../components/sidebarLayout";
import { Container, Row, Col, Button, ListGroup, Form } from "react-bootstrap";
import menuData from "../../data/menuData";
import axios from "axios";
import axiosInstance from "../../api/axios";

export default function QnADetail({ user }) {
  const qnaMenu = menuData.find((menu) => menu.title === "포럼소식");
  const { qnaId } = useParams();
  const navigate = useNavigate();

  const [qna, setQna] = useState(null); // Q&A 데이터
  const [replies, setReplies] = useState([]); // 댓글 데이터
  const [newReply, setNewReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [prevQna, setPrevQna] = useState(null);
  const [nextQna, setNextQna] = useState(null);

  // Q&A 데이터 가져오기
  useEffect(() => {
    const fetchQnA = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`/api/qna/detail/${qnaId}`);
        const data = response?.data?.data || {};
        setQna(data);

        const allQnasResponse = await axios.get("/api/qna/list");
        const allQnas = allQnasResponse?.data?.data || [];
        const currentIndex = allQnas.findIndex(
          (qna) => qna.qnaId === parseInt(qnaId, 10)
        );

        setPrevQna(allQnas[currentIndex - 1] || null);
        setNextQna(allQnas[currentIndex + 1] || null);
      } catch (err) {
        console.error("Error fetching QnA:", err);
        setError("Q&A를 가져오는 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchQnA();
  }, [qnaId]);

  // 댓글 데이터 가져오기
  useEffect(() => {
    const fetchReplies = async () => {
      try {
        const response = await axios.get(`/api/reply/${qnaId}`);
        setReplies(response.data.data || []);
      } catch (err) {
        console.error("Error fetching replies:", err);
        alert("댓글을 가져오는 중 오류가 발생했습니다.");
      }
    };

    fetchReplies();
  }, [qnaId]);

  // 댓글 작성 핸들러
  const handleAddReply = async () => {
    if (!newReply.trim()) {
      alert("댓글 내용을 입력해주세요.");
      return;
    }

    try {
      const response = await axiosInstance.post("/api/reply/write", {
        qnaId: qna.qnaId, // 현재 Q&A 게시글 ID
        content: newReply.trim(), // 댓글 내용
      });

      // 댓글 추가 후 업데이트
      const newReplyData = response.data.data; // 서버에서 받은 새 댓글
      setReplies((prevReplies) => [...prevReplies, newReplyData]);
      setNewReply("");
      alert("댓글이 성공적으로 추가되었습니다.");
    } catch (err) {
      console.error("Error adding reply:", err);
      alert("댓글 작성 중 오류가 발생했습니다.");
    }
  };

  // 삭제 핸들러
  const handleDeleteQnA = async () => {
    if (window.confirm("이 Q&A를 삭제하시겠습니까?")) {
      try {
        await axiosInstance.delete(`/api/qna/delete/${qna.qnaId}`);
        alert("Q&A가 성공적으로 삭제되었습니다.");
        navigate("/qna"); // 삭제 후 목록 페이지로 이동
      } catch (err) {
        console.error("Error deleting QnA:", err);
        alert("Q&A 삭제 중 오류가 발생했습니다.");
      }
    }
  };

  // 댓글 삭제 핸들러
  const handleDeleteReply = async (replyId) => {
    if (window.confirm("이 댓글을 삭제하시겠습니까?")) {
      try {
        await axiosInstance.delete(`/api/reply/delete/${replyId}`);
        setReplies((prevReplies) =>
          prevReplies.filter((reply) => reply.replyId !== replyId)
        );
        alert("댓글이 성공적으로 삭제되었습니다.");
      } catch (err) {
        console.error("Error deleting reply:", err);
        alert("댓글 삭제 중 오류가 발생했습니다.");
      }
    }
  };

  // Q&A 작성자나 관리자만 댓글 작성 가능
  const canAddReply =
    user && (user.userType === "ADMIN" || user.email === qna?.email);

  if (loading) {
    return <p className="text-center">로딩 중...</p>;
  }

  if (error) {
    return <p className="text-center text-danger">{error}</p>;
  }

  if (!qna) {
    return (
      <p className="text-center text-muted">해당 Q&A를 찾을 수 없습니다.</p>
    );
  }

  const subTitle = "Q&A";

  return (
    <div>
      <SidebarLayout menu={qnaMenu} subTitle={subTitle}>
        <Container className="my-5">
          {/* 제목 및 작성자, 작성일자 */}
          <Row className="align-items-center mb-3">
            <Col>
              <h1 className="qna-title">{qna.title}</h1>
            </Col>
            <Col className="text-end text-muted small">
              <p>작성자: {qna.nameKor}</p>
              <p>작성일: {new Date(qna.createdAt).toLocaleDateString()}</p>
            </Col>
          </Row>

          {/* 내용 */}
          <Row className="mb-5">
            <Col>
              <p>{qna.content}</p>
            </Col>
          </Row>

          {/* 삭제 버튼 */}
          {canAddReply && (
            <Row className="mb-3">
              <Col className="text-end">
                <Button
                  variant="danger"
                  onClick={handleDeleteQnA}
                  style={{ marginBottom: "20px" }}
                >
                  삭제
                </Button>
              </Col>
            </Row>
          )}

          {/* 댓글 섹션 */}
          <Row className="mb-3">
            <Col>
              <h5>댓글</h5>
              <ListGroup>
                {replies.length > 0 ? (
                  replies.map((reply, index) => (
                    <ListGroup.Item
                      key={reply.replyId || index}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span>
                        <strong>{reply.nameKor}</strong>: {reply.content}
                      </span>
                      {canAddReply && (
                        <span
                          style={{
                            cursor: "pointer",
                            color: "#aaa",
                            fontSize: "0.9rem",
                          }}
                          onClick={() => handleDeleteReply(reply.replyId)}
                        >
                          삭제
                        </span>
                      )}
                    </ListGroup.Item>
                  ))
                ) : (
                  <ListGroup.Item>댓글이 없습니다.</ListGroup.Item>
                )}
              </ListGroup>
            </Col>
          </Row>

          {/* 댓글 작성 섹션 */}
          {canAddReply ? (
            <Row className="mb-4">
              <Col>
                <h5>댓글 작성</h5>
                <Form>
                  <Form.Group controlId="replyInput">
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="댓글을 입력하세요"
                      value={newReply}
                      onChange={(e) => setNewReply(e.target.value)}
                    />
                  </Form.Group>
                  <Button
                    style={{
                      backgroundColor: "#1769b3",
                      borderColor: "#1769b3",
                      color: "white",
                      marginTop: "10px",
                    }}
                    onClick={handleAddReply}
                  >
                    댓글 작성
                  </Button>
                </Form>
              </Col>
            </Row>
          ) : null}

          {/* 목록으로 돌아가기 버튼 */}
          {/* <Button
            style={{
              backgroundColor: "#1769b3",
              borderColor: "#1769b3",
              color: "white",
            }}
            onClick={() => navigate("/qna")}
          >
            목록으로 돌아가기
          </Button> */}

          <Row className="justify-content-between">
            <Col>
              {prevQna && (
                <Button
                  style={{
                    borderColor: "#1769b3",
                    color: "#1769b3",
                    backgroundColor: "transparent",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#1769b3";
                    e.target.style.color = "#ffffff";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.color = "#1769b3";
                  }}
                  onClick={() => navigate(`/qna/${prevQna.qnaId}`)}
                >
                  이전 글: {prevQna.title}
                </Button>
              )}
            </Col>
            <Col className="text-end">
              {nextQna && (
                <Button
                  style={{
                    borderColor: "#1769b3",
                    color: "#1769b3",
                    backgroundColor: "transparent",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#1769b3";
                    e.target.style.color = "#ffffff";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.color = "#1769b3";
                  }}
                  onClick={() => navigate(`/qna/${nextQna.qnaId}`)}
                >
                  다음 글: {nextQna.title}
                </Button>
              )}
            </Col>
          </Row>
        </Container>
      </SidebarLayout>
    </div>
  );
}
